/* ═══════════════════════════════════════════════════════════════════════
   Dirty-review reader — decision fan-out.

   The reader's multi-select rules are NOT here: Explorer semantics are one
   law for the whole app, so they live in `renderer/lib/listSelection.ts`
   (`nextSelection`) and are shared with the forma canvas and the Spec
   Studio's block layer. The reader passes FLAGGED-ITEM order (the order it
   navigates), never rendered-DOM order.

   formatting-artifacts R1 renderer half (2026-09-03): five kinds, four
   verbs total (Set level, Merge into previous, Delete, Replace with
   standard), every kind also offers Keep. `decisionAppliesTo` is the ONE
   place that says which verb belongs to which kind — the multi-select bar,
   the fallback list, and the Accept-suggested slot all read it, so they can
   never drift apart. */

import type { DirtyItem, DirtyItemKind, DirtyFixDecision } from '../../shared/forma';

/** Structurally identical to `DirtyReviewDialog`'s `DirtyDecision` — defined
    locally (not imported) to avoid a circular import; TS accepts either
    value wherever the other is expected. */
export type Decision = {
  action: 'fix' | 'dismiss' | 'remove' | 'keep' | 'merge' | 'replace' | 'strip-level' | 'flag';
  heading?: string;
};

/** Which kinds a decision is even meaningful for. Keep is universal; every
    other verb belongs to exactly one kind-category:
      - `fix` (Set level) / `merge` (Merge into previous) → unheadered only
      - `remove` (Delete) → empty-paragraph / page-break / legacy-title-table / stale-terminator / pre-body-remnant only (explicit, never suggested)
      - `replace` (Replace with standard) → stale-terminator / pre-body-remnant only
      - `strip-level` (Remove level, 2026-09-08) → empty-paragraph ONLY when it
        carries a level (`hasLevel`) — a table-adjacent blank line that would
        otherwise print as an empty numbered line ("B.").
      - `flag` (Flag for editing, R2 2026-09-08) → every kind, single or
        multi-select. The formatter never touches a spec's CONTENT, so this
        is the reviewer's way of saying "fix this by hand in Word" — it
        counts as decided, like Keep, but the document is untouched either
        way.
    A mixed selection therefore fans a decision out only to the subset of
    items it actually applies to. Takes the ITEM (not just its kind) because
    `strip-level` needs `hasLevel` too. */
export function decisionAppliesTo(
  item: Pick<DirtyItem, 'kind' | 'hasLevel'>,
  action: Decision['action']
): boolean {
  const kind = item.kind ?? 'unheadered';
  if (action === 'keep' || action === 'flag') return true;
  if (action === 'fix') return kind === 'unheadered' || kind === 'typed-list-marker';
  if (action === 'dismiss' || action === 'merge') return kind === 'unheadered';
  if (action === 'remove')
    return (
      kind === 'empty-paragraph' ||
      kind === 'page-break' ||
      kind === 'legacy-title-table' ||
      kind === 'stale-terminator' ||
      kind === 'pre-body-remnant'
    );
  if (action === 'replace') return kind === 'stale-terminator' || kind === 'pre-body-remnant';
  if (action === 'strip-level') return kind === 'empty-paragraph' && !!item.hasLevel;
  return false;
}

/* ─── "Accept suggested" (2026-08-23) ───────────────────────────────────────
   Josh reviews 268 items at a time and the engine's guess is now nearly always
   right, so the review is a spam-through: one button, one place, click until
   you disagree. The rules that make that safe live here, as pure functions,
   because "the button in the fixed slot did something OTHER than the
   suggestion" is the one failure mode a spam-click cannot survive. */

/** The decision that IS this item's suggestion, or null when it has none. */
export type SuggestedDecision =
  | { action: 'fix'; heading: string }
  | { action: 'keep' }
  | { action: 'replace' };

/**
 * ⛔ SPEC_WORKFLOW §13 (Spec Tighten D6, 2026-10-13): the Formatter never
 * deletes an author's paragraph on its own. A blank return or a page break
 * is shown for review and its suggestion is KEEP — Delete (and Remove level)
 * stay one click away as the reviewer's own explicit choice, never the
 * default and never what Enter / a spam-click does.
 *
 * A nonstandard section end or leftover pre-body line's suggestion is
 * Replace with standard — the engine's own auto-normalize already declined
 * to touch it, so the reviewer's job is to confirm the swap or Keep it.
 *
 * An unstyled paragraph is only suggestible when the engine actually had a
 * signal — no signal stays no suggestion, never a guess.
 */
export function suggestedDecisionFor(item: DirtyItem): SuggestedDecision | null {
  const kind = item.kind ?? 'unheadered';
  if (
    kind === 'empty-paragraph' ||
    kind === 'page-break' ||
    kind === 'legacy-title-table' ||
    kind === 'typed-list-marker'
  )
    return { action: 'keep' };
  if (kind === 'stale-terminator' || kind === 'pre-body-remnant') return { action: 'replace' };
  const level = item.suggestedLevel;
  if (typeof level !== 'number' || !Number.isInteger(level) || level < 1 || level > 9) return null;
  return { action: 'fix', heading: `Heading${level}` };
}

export type AcceptPlan = {
  /** Exactly what the button will do, in selection order. Empty ⇒ the slot
      renders its disabled placeholder and a click is a no-op. */
  targets: { id: string; decision: SuggestedDecision }[];
  /** What the fixed slot reads — the level is ON the button, so a spam-click
      can never be a surprise. */
  label: string;
};

const NO_SUGGESTION_LABEL = 'No suggestion';

function suggestedLabel(decision: SuggestedDecision): string {
  if (decision.action === 'keep') return 'Accept · Keep';
  if (decision.action === 'replace') return 'Accept · Replace with standard';
  return `Accept · H${decision.heading.replace(/\D/g, '')}`;
}

/**
 * What "Accept suggested" does for the current selection.
 *
 * One item  → its own suggestion, named on the button ("Accept · H4",
 *             "Accept · Keep", "Accept · Replace with standard"), or the
 *             disabled placeholder.
 * Many      → each item's OWN suggestion, applied item by item; the ones with
 *             no suggestion are left untouched rather than guessed at, and the
 *             count on the button is the number that will actually change.
 */
export function acceptSuggestedPlan(items: DirtyItem[]): AcceptPlan {
  const targets = items
    .map((item) => ({ id: item.id, decision: suggestedDecisionFor(item) }))
    .filter((t): t is { id: string; decision: SuggestedDecision } => !!t.id && !!t.decision);

  if (items.length === 1) {
    const only = targets[0];
    if (!only) return { targets: [], label: NO_SUGGESTION_LABEL };
    return { targets, label: suggestedLabel(only.decision) };
  }
  if (targets.length === 0) return { targets: [], label: NO_SUGGESTION_LABEL };
  return { targets, label: `Accept suggested (${targets.length})` };
}

/* ─── Decision → engine directive + report bookkeeping ──────────────────── */

/** How many of `samples` still have no decision recorded — the T7 "no
    silent shipping" count, and the same math the per-file tab badges and
    the footer's "Leave the rest as-is" button use. One brain: the dialog,
    doFormat's ship-with-unresolved gate, and the unit test below all call
    this instead of re-deriving the filter.

    Every item counts (D6, 2026-10-13) — the engine decides nothing itself,
    so blank returns and page breaks are questions like any other kind. */
export function countUndecided(
  samples: Pick<DirtyItem, 'id'>[],
  decisions: Record<string, Decision>
): number {
  return samples.filter((s) => !s.id || !decisions[s.id]).length;
}

/** "Delete all blank returns" / "Delete all page breaks" (D6): the ids of
    every still-UNDECIDED item of `kind` — an item the reviewer already
    decided (Keep included) is never overridden. The caller records an
    explicit `remove` for each; nothing is removed by default. Pure. */
export function bulkDeleteTargets(
  samples: Pick<DirtyItem, 'id' | 'kind'>[],
  decisions: Record<string, Decision>,
  kind: 'empty-paragraph' | 'page-break'
): string[] {
  return samples
    .filter((s): s is typeof s & { id: string } => !!s.id && (s.kind ?? 'unheadered') === kind)
    .filter((s) => !decisions[s.id])
    .map((s) => s.id);
}

/**
 * A file's review decisions, translated into what `formatDocuments` actually
 * applies to the document. `keep`, `flag`, and the legacy `dismiss` no-op
 * leave no trace here — the document is untouched either way, and the choice
 * survives only in the report's decision map (recorded separately, verbatim).
 *
 * ⛔ `remove` is emitted ONLY for an explicit reviewer decision (D6,
 * 2026-10-13 — SPEC_WORKFLOW §13). An undecided item emits nothing: Skip,
 * close, or "Keep all remaining" all leave the document as written.
 */
export function toEngineDecisions(
  samples: DirtyItem[],
  decisions: Record<string, Decision>
): DirtyFixDecision[] {
  const out: DirtyFixDecision[] = [];
  for (const s of samples) {
    if (!s.id) continue;
    const d = decisions[s.id];
    if (!d) continue;
    if (d.action === 'remove') {
      out.push({ id: s.id, action: 'remove' });
    } else if (d.action === 'strip-level') {
      out.push({ id: s.id, action: 'strip-level' });
    } else if (d.action === 'merge') {
      out.push({ id: s.id, action: 'merge-prev' });
    } else if (d.action === 'replace') {
      out.push({
        id: s.id,
        action: 'replace-standard',
        standardReplacement: s.standardReplacement,
      });
    } else if (d.action === 'fix' && d.heading) {
      out.push({ id: s.id, action: 'heading', heading: d.heading });
    }
  }
  return out;
}
